import * as React from 'react';
import { Link, useLocation } from 'react-router';
import {
  Menu,
  X,
  ChevronDown,
  ArrowUpRight,
  ArrowRight,
  CalendarDays,
} from 'lucide-react';

import { WarpedTexture } from '@/components/three/SecurityCardTexture';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { OpenAppButton } from '@/components/ui/open-app-dialog';
import { BookDemoButton, useBookDemo } from '@/components/ui/book-demo-dialog';
import { Container } from '@/components/ui/container';
import { Logo } from '@/components/ui/logo';
import { getIcon } from '@/components/ui/icon-map';
import { navMenus, type NavLink, type NavMenu } from '@/content/site';
import { trackDocsClick } from '@/lib/analytics';

/** A large, bordered "card" link used inside the full-width desktop mega-panel. */
function MegaCard({ item, onClick }: { item: NavLink; onClick?: () => void }) {
  const Icon = getIcon(item.icon);
  const inner = (
    <>
      <span className="grid size-10 shrink-0 place-items-center rounded-md border-none bg-none text-slate-700 transition-colors group-hover:border-ember-200 group-hover:text-ember-600">
        <Icon className="size-5" />
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1 text-[0.95rem] font-semibold text-ink-900">
          {item.label}
          {item.external && (
            <ArrowUpRight className="size-3.5 text-ink-300 transition-colors group-hover:text-ember-500" />
          )}
        </span>
        <span className="mt-1 block text-sm leading-snug text-ink-500">
          {item.description}
        </span>
      </span>
    </>
  );
  const className =
    'group flex items-start gap-3.5 rounded-lg border bg-zinc-50 border-white shadow-soft-sm p-4 hover:bg-ember-50/50';
  return item.external ? (
    <a
      href={item.href}
      target="_blank"
      rel="noreferrer"
      className={className}
      onClick={() => {
        trackDocsClick(item.href, 'header_desktop');
        onClick?.();
      }}
    >
      {inner}
    </a>
  ) : (
    <Link to={item.href} className={className} onClick={onClick}>
      {inner}
    </Link>
  );
}

/** A compact icon + label + description row used inside the mobile menu. */
function MenuRow({ item, onClick }: { item: NavLink; onClick?: () => void }) {
  const Icon = getIcon(item.icon);
  const inner = (
    <>
      <span className="grid size-9 shrink-0 place-items-center rounded-md text-slate-700 transition-colors group-hover:text-ember-600 group-active:text-ember-600">
        <Icon className="size-[1.05rem]" />
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1 text-sm font-semibold text-ink-900">
          {item.label}
          {item.external && (
            <ArrowUpRight className="size-3.5 text-ink-300 transition-colors group-hover:text-ember-500 group-active:text-ember-500" />
          )}
        </span>
        <span className="mt-0.5 block text-xs leading-snug text-ink-500">
          {item.description}
        </span>
      </span>
    </>
  );
  // Mirror the desktop MegaCard chrome (zinc card, white border, soft shadow,
  // ember hover/tap) so the mobile menu reads the same; only the text is smaller.
  const className =
    'group flex items-start gap-3 rounded-lg border border-white bg-zinc-50 p-3.5 shadow-soft-sm transition-colors hover:bg-ember-50/50 active:bg-ember-50';
  return item.external ? (
    <a
      href={item.href}
      target="_blank"
      rel="noreferrer"
      className={className}
      onClick={() => {
        trackDocsClick(item.href, 'header_mobile');
        onClick?.();
      }}
    >
      {inner}
    </a>
  ) : (
    <Link to={item.href} className={className} onClick={onClick}>
      {inner}
    </Link>
  );
}

function needsMenuGridCta(menu: NavMenu) {
  return menu.items.length % 2 === 1;
}

/** A full-card CTA with a smaller visual control inset inside the hit target. */
function MenuDemoCta({
  menu,
  onClick,
  renderTexture = true,
}: {
  menu: NavMenu;
  onClick: () => void;
  renderTexture?: boolean;
}) {
  const texturePreset =
    menu.label === 'Solutions' ? 'assistantDark' : 'assistantGreen';

  return (
    <div className="group relative isolate h-full min-h-16 w-full self-stretch rounded-lg border border-hairline bg-white transition-colors duration-300 hover:border-ember-300">
      <div
        aria-hidden
        data-overlay="assistant"
        className="capability-detail-texture pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
      >
        {renderTexture && (
          <WarpedTexture
            preset={texturePreset}
            className="opacity-95 transition-transform duration-500 group-hover:scale-105"
          />
        )}
      </div>

      <BookDemoButton
        analyticsLocation={`header_menu_${menu.label.toLowerCase()}`}
        aria-label={`Book a demo from the ${menu.label} menu`}
        variant="outline"
        onClick={onClick}
        className="relative z-10 h-full min-h-16 w-full justify-start rounded-[inherit] border-0 bg-transparent p-4 font-medium text-white shadow-none hover:bg-white/5 hover:text-white active:bg-white/10 focus-visible:ring-inset focus-visible:ring-ember-400/40 focus-visible:ring-offset-0"
      >
        <span className="pointer-events-none flex h-full w-full items-center gap-3 rounded-md border border-white/35 bg-slate-950/35 px-2 text-left backdrop-blur-[3px] transition-all duration-300 group-hover:-translate-y-px group-hover:border-white/60 group-hover:bg-slate-950/50 group-active:translate-y-0 group-active:bg-slate-950/60 lg:gap-3.5">
          <span className="grid size-9 shrink-0 place-items-center rounded-md border-none border-white/15 bg-white/0 text-white/90 transition-colors group-hover:text-white lg:size-10">
            <CalendarDays className="size-[1.05rem] lg:size-5" />
          </span>
          <span className="flex items-center gap-1.5 font-semibold">
            Book a demo
            <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
          </span>
        </span>
      </BookDemoButton>
    </div>
  );
}

/** The full-width dropdown panel for a single nav menu. */
function MegaPanel({
  menu,
  onNavigate,
  onBookDemo,
}: {
  menu: NavMenu;
  onNavigate: () => void;
  onBookDemo: () => void;
}) {
  // Wider menus (Product/Security) get a 3-up grid; shorter ones (Solutions/
  // Developers) get a roomier 2-up grid so every card stays large.
  const gridCols =
    menu.items.length >= 5 ? 'sm:grid-cols-2 lg:grid-cols-2' : 'sm:grid-cols-2';
  return (
    <Container className="grid gap-x-10 gap-y-6 py-8 lg:grid-cols-[minmax(0,17rem)_1fr]">
      <div className="flex flex-col">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-ember-600">
          {menu.label}
        </span>
        {menu.tagline && (
          <p className="mt-3 max-w-xs text-sm leading-relaxed text-ink-600">
            {menu.tagline}
          </p>
        )}
        <button
          type="button"
          onClick={() => {
            onNavigate();
            onBookDemo();
          }}
          className="mt-5 inline-flex w-fit items-center gap-1.5 text-sm font-medium text-ink-900 transition-colors hover:text-ember-600"
        >
          Book a demo
          <ArrowRight className="size-4" />
        </button>
      </div>
      <div className={cn('grid gap-3', gridCols)}>
        {menu.items.map((item) => (
          <MegaCard key={item.label} item={item} onClick={onNavigate} />
        ))}
        {needsMenuGridCta(menu) && (
          <MenuDemoCta menu={menu} onClick={onNavigate} />
        )}
      </div>
    </Container>
  );
}

export function Header() {
  const [scrolled, setScrolled] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [menu, setMenu] = React.useState<string | null>(null);
  const location = useLocation();

  const activeMenu = navMenus.find((m) => m.label === menu) ?? null;
  const { open: openBookDemo } = useBookDemo();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menus on navigation.
  React.useEffect(() => {
    setMenu(null);
    setOpen(false);
  }, [location.pathname]);

  // Close either navigation menu on Escape.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenu(null);
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  React.useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <header
      onMouseLeave={() => setMenu(null)}
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-colors duration-300',
        scrolled || open || activeMenu
          ? 'border-b border-hairline'
          : 'border-b border-transparent',
        activeMenu
          ? 'bg-white'
          : scrolled || open
            ? 'bg-white/85 backdrop-blur-md'
            : ''
      )}
    >
      <Container className="relative z-20 flex h-16 items-center justify-between gap-4 sm:h-[4.5rem]">
        <Link to="/" className="shrink-0" aria-label="Den home">
          <Logo />
        </Link>

        {/* Desktop mega-menu nav */}
        <nav
          aria-label="Primary navigation"
          className="hidden items-center gap-1 lg:flex"
        >
          {navMenus.map((m) => (
            <button
              key={m.label}
              type="button"
              aria-haspopup="true"
              aria-expanded={menu === m.label}
              onMouseEnter={() => setMenu(m.label)}
              onClick={(event) =>
                // Pointer entry already opens the panel. Keep a subsequent
                // click open, while keyboard activation can toggle it.
                setMenu((cur) =>
                  event.detail === 0 && cur === m.label ? null : m.label
                )
              }
              className={cn(
                'flex items-center gap-1 rounded-full px-3.5 py-2 text-sm font-medium transition-colors',
                menu === m.label
                  ? 'text-ink-900'
                  : 'text-ink-500 hover:text-ink-900'
              )}
            >
              {m.label}
              <ChevronDown
                className={cn(
                  'size-3.5 transition-transform duration-200',
                  menu === m.label && 'rotate-180'
                )}
              />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <OpenAppButton
            variant="ghost"
            size="sm"
            analyticsLocation="header_desktop"
          >
            Open app
          </OpenAppButton>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => openBookDemo('header_desktop')}
          >
            Book a demo
          </Button>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="mobile-navigation"
          className="grid size-10 place-items-center rounded-full text-ink-900 transition-colors hover:bg-ink-50 lg:hidden"
        >
          {open ? (
            <X className="size-5" aria-hidden="true" />
          ) : (
            <Menu className="size-5" aria-hidden="true" />
          )}
        </button>
      </Container>

      {/* Full-width desktop mega-panel */}
      {activeMenu && (
        <>
          {/* Dark, blurred backdrop over the rest of the page (below the header)
              to separate the open dropdown from the content behind it. */}
          <div
            aria-hidden
            onMouseEnter={() => setMenu(null)}
            className="fixed inset-x-0 bottom-0 top-[4.5rem] -z-10 hidden bg-zinc-700/60 backdrop-blur-xl lg:block"
          />
          <div className="absolute inset-x-0 top-full hidden border-b border-hairline bg-white shadow-lift lg:block">
            <MegaPanel
              menu={activeMenu}
              onNavigate={() => setMenu(null)}
              onBookDemo={() =>
                openBookDemo(`header_menu_${activeMenu.label.toLowerCase()}`)
              }
            />
          </div>
        </>
      )}

      {/* Mobile menu backdrop — dims the page behind the open menu; tap to close. */}
      <div
        aria-hidden
        onClick={() => setOpen(false)}
        className={cn(
          'fixed inset-x-0 bottom-0 top-16 z-0 bg-ink-900/25 backdrop-blur-sm transition-opacity duration-300 sm:top-[4.5rem] lg:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
      />

      {/* Mobile menu */}
      <nav
        id="mobile-navigation"
        aria-label="Mobile primary navigation"
        inert={!open}
        aria-hidden={!open}
        className={cn(
          'relative z-10 flex flex-col overflow-hidden border-t border-hairline bg-white transition-[max-height,opacity] duration-300 lg:hidden',
          open
            ? 'max-h-[calc(100dvh_-_4rem)] opacity-100'
            : 'max-h-0 border-transparent opacity-0'
        )}
      >
        {/* Scrollable nav region. The inner flex-1 + min-h-0 child does the
            scrolling (flex sizing, not a percentage height — a percentage would
            collapse since the menu's height comes from max-h clamping) while the
            CTA footer below stays pinned. */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <Container className="flex flex-col gap-5 py-5">
              {navMenus.map((m) => (
                <div key={m.label}>
                  <div className="px-1 text-xs font-semibold uppercase tracking-[0.16em] text-ink-400">
                    {m.label}
                  </div>
                  <div className="mt-2.5 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    {m.items.map((item) => (
                      <MenuRow
                        key={item.label}
                        item={item}
                        onClick={() => setOpen(false)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </Container>
          </div>
          {/* Scroll-affordance fades pinned to the top/bottom of the scroll area. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-white to-transparent"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent"
          />
        </div>

        {/* CTAs pinned to the bottom so they're always reachable. */}
        <div className="shrink-0 border-t border-hairline bg-white">
          <Container className="flex flex-col gap-2.5 py-4">
            <OpenAppButton
              analyticsLocation="header_mobile"
              variant="outline"
              size="lg"
              onClick={() => setOpen(false)}
            >
              Open the app
            </OpenAppButton>
            <Button
              type="button"
              variant="primary"
              size="lg"
              onClick={() => {
                setOpen(false);
                openBookDemo('header_mobile');
              }}
            >
              Book a demo
            </Button>
          </Container>
        </div>
      </nav>
    </header>
  );
}
